import React from 'react';
import { PRINTER_ADAPTER_MANIFEST_URL } from '../constants/printer-adapter';
import { PrinterAdapterManifest, PrinterAdapterRelease } from '../models/printer-adapter';

interface UsePrinterAdapterInstallerResult {
  /** The current release, or null while loading and whenever the manifest could not be used. */
  release: PrinterAdapterRelease | null;
  isBusy: boolean;
  /** True once the fetch has settled, successfully or not. */
  loaded: boolean;
  /** True when there is no installer to offer - unreachable, unreadable or invalid manifest. */
  failed: boolean;
}

/**
 * Reads the adapter's release manifest so the download button always points at the current
 * installer, rather than at a URL configured per deployment that nobody updates on release.
 *
 * Validation mirrors `UpdateManifest.TryValidate` in `hs-printer-adapter` - a version, an absolute
 * https URL and 64 hex characters of SHA-256 - and, like the adapter, anything malformed means
 * "no update available" rather than an error the user has to act on: handing someone a broken
 * installer link is worse than showing no link at all.
 */
export const usePrinterAdapterInstaller = (): UsePrinterAdapterInstallerResult => {
  const [release, setRelease] = React.useState<PrinterAdapterRelease | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    setIsBusy(true);

    // `no-store` for the same reason CI publishes the manifest with `no-cache`: a release must be
    // visible to the next reader, not after an edge cache expires. No credentials - the manifest
    // container is anonymously readable and the JWT has no business leaving the API's origin.
    fetch(PRINTER_ADAPTER_MANIFEST_URL, { cache: 'no-store', credentials: 'omit' })
      .then((response) => (response.ok ? response.json() : null))
      .then((manifest: PrinterAdapterManifest | null) => {
        if (cancelled) return;
        setRelease(toRelease(manifest));
      })
      .catch(() => {
        if (cancelled) return;
        setRelease(null);
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { release, isBusy, loaded, failed: loaded && !release };
};

const toRelease = (manifest: PrinterAdapterManifest | null): PrinterAdapterRelease | null => {
  const version = manifest?.version?.trim();
  const url = manifest?.url?.trim();
  const sha256 = manifest?.sha256?.trim();

  if (!version || !url || !sha256) return null;
  if (!isHttpsUrl(url)) return null;
  if (!isSha256Hex(sha256)) return null;

  return { version, url, sha256, releasedAt: manifest?.releasedAt };
};

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
};

const isSha256Hex = (value: string): boolean => /^[0-9a-f]{64}$/i.test(value);
